import {
    ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined, EyeOutlined,
    FileExcelOutlined, FilePdfOutlined,
    InboxOutlined, PercentageOutlined, PlusOutlined, SearchOutlined, UploadOutlined
} from '@ant-design/icons';
import {
    Alert, Button, Card, Col, ConfigProvider, Empty, Input, Modal, Row,
    Space, Spin, Table, Tag, Tooltip, Typography, Upload, message
} from 'antd';
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import { LOGGED_USER } from '../../../variables/constants';
import { peutModifierRetenueFiscale } from '../../../services/rightsService';
import RetenueFiscaleModal from '../retenue/RetenueFiscaleModal';
import axiosInstance from '../../../variables/api/axiosInstance';
import axiosMultiPartDataInstance from '../../../variables/api/axiosMultiPartDatainstance';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY,
} from '../../stats/sinistre/etatsSinistresFunctions';

const ACCENT = "#242166";

/**
 * Gestion des courtiers (v2 de /courtiers) : liste consolidée + accès aux fiches +
 * import de la liste officielle (Excel).
 *
 * L'import est réservé aux profils « superadmin » DU POOL. Le bouton n'est affiché qu'à
 * eux, mais la garde qui fait foi est côté serveur (CourtierImportService) : masquer un
 * bouton ne protège rien.
 */
const CourtiersV2 = () => {
    const navigate = useNavigate();
    const [courtiers, setCourtiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recherche, setRecherche] = useState("");
    // Taux de retenue : le Pool règle le taux par défaut, une compagnie le sien (convention).
    const [retenueModalOpen, setRetenueModalOpen] = useState(false);

    // Import
    const [importOpen, setImportOpen] = useState(false);
    const [fichier, setFichier] = useState(null);
    const [apercu, setApercu] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [rapport, setRapport] = useState(null);

    const peutImporter = useMemo(() => {
        const nomProfil = (LOGGED_USER?.profil?.nom || "").toLowerCase();
        const estPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
        return nomProfil.includes("superadmin") && estPool;
    }, []);

    const chargerCourtiers = () => {
        setLoading(true);
        axiosInstance
            .get(`/api/agence/all/courtiers`)
            .then((res) => setCourtiers(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => { chargerCourtiers(); }, []);

    // Code courtier numérique (4xx) — comparé en nombre, pas en texte : en tri alphabétique
    // "99" passerait après "401". Null (courtier pas encore validé, donc sans code) = -1.
    const codeNum = (c) => {
        const n = parseInt(c?.codeAgence, 10);
        return Number.isNaN(n) ? -1 : n;
    };

    const donnees = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        const filtres = !q ? courtiers : courtiers.filter((c) =>
            [c?.nom, c?.codeAgence, c?.email, c?.responsable, c?.referenceArrete]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
        // Tri par défaut : du PLUS RÉCENT au plus ancien. La date de création fait foi (elle
        // existe toujours) ; le code courtier, séquentiel, départage à date égale.
        return [...filtres].sort((a, b) => {
            const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (db !== da) return db - da;
            return codeNum(b) - codeNum(a);
        });
    }, [courtiers, recherche]);

    // ── Exports ────────────────────────────────────────────────────────────────
    // Portent sur `donnees` : ce qui est EXPORTÉ est exactement ce qui est affiché,
    // filtre de recherche compris (exporter une liste qui ne correspond pas à l'écran
    // est le meilleur moyen de produire un document qu'on croit complet et qui ne l'est pas).

    /** Contacts d'un courtier : téléphones puis e-mail, comme sur la liste officielle. */
    const contactsDe = (c) => [
        ...(Array.isArray(c?.contact) ? c.contact : []),
        c?.telephone1Agence, c?.telephone2Agence,
    ].filter(Boolean).join(" / ");

    const horodatage = () => dayjs().format("YYYY-MM-DD_HH[h]mm");

    const exportExcel = () => {
        const lignes = donnees.map((c, i) => ({
            "N°": i + 1,
            "Code": c?.codeAgence || "",
            "Dénomination": c?.nom || "",
            "Références Arrêté": c?.referenceArrete || "",
            "Contacts": contactsDe(c),
            "E-mail": c?.email || "",
            "Principaux dirigeants": c?.responsable || "",
            "Ville": c?.ville || "",
            "Agréments validés": c?.validAgrements?.length || 0,
            "Agréments en attente": Math.max(0, (c?.agrements?.length || 0) - (c?.validAgrements?.length || 0)),
        }));
        const ws = XLSX.utils.json_to_sheet(lignes);
        ws["!cols"] = [{ wch: 5 }, { wch: 8 }, { wch: 40 }, { wch: 32 }, { wch: 28 },
                       { wch: 30 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 12 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Courtiers");
        XLSX.writeFile(wb, `liste-des-courtiers-${horodatage()}.xlsx`);
    };

    /**
     * PDF calqué sur la LISTE OFFICIELLE du Ministère (mêmes colonnes, même portrait) :
     * N° / Dénomination / Références Arrêté / Contacts-E-mail / Principaux dirigeants.
     * L'en-tête reste celui du Pool — reproduire l'en-tête de la République Togolaise
     * ferait passer un export applicatif pour l'arrêté ministériel lui-même.
     */
    const exportPDF = async () => {
        const doc = new jsPDF({ orientation: "portrait" });
        let enteteData = null;
        let logos = [];
        try {
            [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
        } catch (e) { /* entête/logos optionnels : le tableau doit sortir quoi qu'il arrive */ }
        const formattedNow = dayjs().format("DD/MM/YYYY HH:mm");

        autoTable(doc, {
            startY: 40,
            margin: { top: 38, bottom: 20, left: 12, right: 12 },
            head: [["N°", "Dénomination", "Références Arrêté portant autorisation\nde l'exercice de courtage",
                    "Contacts / E-mail", "Nom du/des principaux dirigeants"]],
            body: donnees.map((c, i) => [
                i + 1,
                c?.nom || "",
                c?.referenceArrete || "",
                [contactsDe(c), c?.email].filter(Boolean).join("\n"),
                c?.responsable || "",
            ]),
            styles: { fontSize: 8, cellPadding: 2, valign: "middle", lineWidth: 0.1, lineColor: [120, 120, 120] },
            headStyles: { fillColor: NAVY, fontSize: 8, halign: "center" },
            columnStyles: {
                0: { cellWidth: 10, halign: "center" },
                1: { cellWidth: 42 },
                2: { cellWidth: 40, halign: "center" },
                3: { cellWidth: 50 },
                4: { cellWidth: 44 },
            },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `LISTE DES COURTIERS ${dayjs().year()}`,
                subtitle: `${donnees.length} courtier(s) — autorisés à présenter ou à souscrire des contrats `
                    + `pour le compte d'une entreprise d'assurance agréée`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        doc.save(`liste-des-courtiers-${horodatage()}.pdf`);
    };

    const columns = [
        {
            title: "Code",
            dataIndex: "codeAgence",
            width: 90,
            render: (v) => v ? <Tag color="blue">{v}</Tag> : <Tag>—</Tag>,
            sorter: (a, b) => codeNum(a) - codeNum(b),
        },
        {
            title: "Dénomination",
            dataIndex: "nom",
            render: (v, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: ACCENT }}>{v || "—"}</div>
                    {r?.referenceArrete && (
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{r.referenceArrete}</div>
                    )}
                </div>
            ),
            sorter: (a, b) => (a.nom || "").localeCompare(b.nom || ""),
        },
        { title: "Responsable", dataIndex: "responsable", render: (v) => v || "—" },
        { title: "Email", dataIndex: "email", render: (v) => v || <span style={{ color: "#bfbfbf" }}>—</span> },
        {
            title: "Agréments",
            key: "agrements",
            render: (_, r) => {
                const valides = r?.validAgrements?.length || 0;
                const total = r?.agrements?.length || 0;
                return (
                    <Space size={4}>
                        <Tag icon={<CheckCircleOutlined />} color="green">{valides} validé{valides > 1 ? "s" : ""}</Tag>
                        <Tag icon={<ClockCircleOutlined />} color="orange">
                            {Math.max(0, total - valides)} en attente
                        </Tag>
                    </Space>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 110,
            render: (_, r) => (
                <Tooltip title="Ouvrir la fiche du courtier">
                    <Button type="primary" ghost icon={<EyeOutlined />}
                        onClick={() => navigate(`/courtier/${r.codeId}`)}>
                        Détails
                    </Button>
                </Tooltip>
            ),
        },
    ];

    // ── Import ────────────────────────────────────────────────────────────
    const lancerApercu = (file) => {
        setFichier(file);
        setApercu(null);
        setRapport(null);
        const fd = new FormData();
        fd.append("file", file);
        setEnCours(true);
        axiosMultiPartDataInstance
            .post(`/api/courtiers/import/preview`, fd)
            .then((res) => setApercu(res.data))
            .catch((e) => {
                const msg = e?.response?.data?.message || e?.response?.data?.error || "Fichier illisible ou accès refusé.";
                message.error(msg);
            })
            .finally(() => setEnCours(false));
        return false; // on gère l'upload nous-mêmes
    };

    const confirmerImport = () => {
        if (!fichier) return;
        const fd = new FormData();
        fd.append("file", fichier);
        setEnCours(true);
        axiosMultiPartDataInstance
            .post(`/api/courtiers/import/confirm`, fd)
            .then((res) => {
                setRapport(res.data);
                message.success(`${res.data?.createdRows || 0} courtier(s) créé(s).`);
                chargerCourtiers();
            })
            .catch((e) => {
                const msg = e?.response?.data?.message || "Échec de l'import.";
                message.error(msg);
            })
            .finally(() => setEnCours(false));
    };

    const fermerImport = () => {
        setImportOpen(false);
        setFichier(null);
        setApercu(null);
        setRapport(null);
    };

    const colonnesApercu = [
        { title: "Ligne", dataIndex: "rowNumber", width: 60 },
        { title: "Dénomination", dataIndex: "denomination" },
        { title: "Responsable", dataIndex: "dirigeant", render: (v) => v || "—" },
        {
            // Les numéros sont découpés à l'import (« a / b » → 2 contacts) : on montre le
            // résultat du découpage, pas la chaîne brute, pour que l'écart saute aux yeux.
            title: "Contacts",
            dataIndex: "contacts",
            render: (v) => {
                const nums = String(v || "").split(/[/;,]/).map((s) => s.trim().replace(/\s+/g, " ")).filter(Boolean);
                if (!nums.length) return <span style={{ color: "#bfbfbf" }}>—</span>;
                return (
                    <Space size={[0, 4]} wrap>
                        {nums.map((n, i) => <Tag key={i} color={i < 2 ? "blue" : "default"}>{n}</Tag>)}
                    </Space>
                );
            },
        },
        { title: "Email", dataIndex: "email", render: (v) => v || <span style={{ color: "#bfbfbf" }}>—</span> },
        {
            title: "Code", dataIndex: "codeAgence", width: 80,
            render: (v) => v ? <Tag color="blue">{v}</Tag> : "—",
        },
        {
            title: "Statut",
            key: "statut",
            width: 150,
            render: (_, r) => {
                if (r.createdCourtierCodeId) return <Tag color="green">Créé</Tag>;
                if (r.doublon) return <Tag color="orange">Déjà existant</Tag>;
                if (r.valid) return <Tag color="blue">À créer</Tag>;
                return <Tag color="red">Erreur</Tag>;
            },
        },
        {
            title: "Détail",
            key: "errors",
            render: (_, r) => (
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {(r.errors || []).join(" ")}
                    {r.compteCree ? " Compte créé." : ""}
                    {r.agrementsCrees ? ` ${r.agrementsCrees} agrément(s).` : ""}
                </div>
            ),
        },
    ];

    const resultat = rapport || apercu;

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">

                                <Space style={{ marginBottom: 16 }}>
                                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/reseau/suivi-courtiers")}>
                                        Retour au suivi
                                    </Button>
                                </Space>

                                <Card
                                    style={{ borderRadius: 12 }}
                                    title={
                                        <Typography.Title level={4} style={{ margin: 0, color: ACCENT }}>
                                            Gestion des courtiers
                                        </Typography.Title>
                                    }
                                    extra={
                                        <Space wrap>
                                            <Input
                                                allowClear
                                                prefix={<SearchOutlined />}
                                                placeholder="Rechercher (nom, code, email, dirigeant…)"
                                                value={recherche}
                                                onChange={(e) => setRecherche(e.target.value)}
                                                style={{ width: 320 }}
                                            />
                                            <Button icon={<FileExcelOutlined />} onClick={exportExcel}
                                                disabled={!donnees.length}>
                                                Excel
                                            </Button>
                                            <Button icon={<FilePdfOutlined />} onClick={exportPDF}
                                                disabled={!donnees.length}>
                                                PDF
                                            </Button>
                                            {peutModifierRetenueFiscale() && (
                                                <Button icon={<PercentageOutlined />} onClick={() => setRetenueModalOpen(true)}>
                                                    Taux de retenue fiscale
                                                </Button>
                                            )}
                                            {peutImporter && (
                                                <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                                                    Importer la liste officielle
                                                </Button>
                                            )}
                                            <Button type="primary" icon={<PlusOutlined />}
                                                style={{ background: ACCENT, borderColor: ACCENT }}
                                                onClick={() => navigate("/ajouter-courtier")}>
                                                Ajouter un courtier
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <Spin spinning={loading}>
                                        <Table
                                            rowKey="codeId"
                                            columns={columns}
                                            dataSource={donnees}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 20 }}
                                            locale={{ emptyText: <Empty description="Aucun courtier" /> }}
                                            scroll={{ x: 900 }}
                                        />
                                    </Spin>
                                </Card>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Modale d'import ── */}
            <Modal
                title="Importer la liste officielle des courtiers"
                open={importOpen}
                onCancel={fermerImport}
                width={1000}
                footer={[
                    <Button key="close" onClick={fermerImport}>Fermer</Button>,
                    !rapport && (
                        <Button
                            key="ok"
                            type="primary"
                            loading={enCours}
                            disabled={!apercu || !apercu.validRows}
                            style={{ background: ACCENT, borderColor: ACCENT }}
                            onClick={confirmerImport}
                        >
                            Importer {apercu?.validRows || 0} courtier(s)
                        </Button>
                    ),
                ]}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Les courtiers importés sont rattachés au Pool et reçoivent un agrément EN ATTENTE pour chaque compagnie active — c'est à chaque compagnie de les valider. Un compte est créé pour chaque ligne comportant un email ; les lignes déjà présentes en base sont ignorées."
                />

                {!apercu && !rapport && (
                    <Upload.Dragger
                        accept=".xlsx,.xls"
                        maxCount={1}
                        beforeUpload={lancerApercu}
                        showUploadList={false}
                        disabled={enCours}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: ACCENT }} /></p>
                        <p className="ant-upload-text">Cliquez ou déposez le fichier Excel ici</p>
                        <p className="ant-upload-hint">
                            Colonnes attendues : Dénomination, Référence Arrêté, Contacts, Email, Dirigeant(s).
                        </p>
                    </Upload.Dragger>
                )}

                <Spin spinning={enCours}>
                    {resultat && (
                        <>
                            <Space wrap style={{ marginBottom: 12 }}>
                                <Tag>{resultat.totalRows} ligne(s)</Tag>
                                {rapport ? (
                                    <>
                                        <Tag color="green">{rapport.createdRows} créé(s)</Tag>
                                        <Tag color="orange">{rapport.skippedRows} ignoré(s)</Tag>
                                        <Tag color="red">{rapport.failedRows} en erreur</Tag>
                                        <Tag color="blue">{rapport.comptesCrees} compte(s) créé(s)</Tag>
                                    </>
                                ) : (
                                    <>
                                        <Tag color="blue">{apercu.validRows} à créer</Tag>
                                        <Tag color="orange">{apercu.doublonRows} déjà existant(s)</Tag>
                                        <Tag color="red">{apercu.invalidRows} en erreur</Tag>
                                    </>
                                )}
                            </Space>
                            {!rapport && apercu?.compagnies?.length > 0 && (
                                <div style={{ marginBottom: 12, fontSize: 12, color: "#8c8c8c" }}>
                                    Agréments créés (en attente) pour : {apercu.compagnies.join(", ")}
                                </div>
                            )}
                            <Table
                                size="small"
                                rowKey="rowNumber"
                                columns={colonnesApercu}
                                dataSource={resultat.rows || []}
                                pagination={false}
                                scroll={{ x: 900, y: 380 }}
                            />
                        </>
                    )}
                </Spin>
            </Modal>
            {peutModifierRetenueFiscale() && (
                <RetenueFiscaleModal
                    nature="COURTIER"
                    open={retenueModalOpen}
                    onClose={() => setRetenueModalOpen(false)}
                />
            )}
        </ConfigProvider>
    );
};

export default CourtiersV2;
